import React, { useEffect, useRef } from "react";
import { Table, Spinner } from "flowbite-react";
import { MdOutlinePersonOff } from "react-icons/md";
import formatDate from "../../Admin/Helpers/formatDate.jsx";

const LeaderboardStaff = ({
  leaderboardError,
  leaderboardData,
  onLoadMore,
  hasMore,
  isFetchingMore,
}) => {
  const loadMoreSentinelRef = useRef(null);

  useEffect(() => {
    if (!onLoadMore || !hasMore || isFetchingMore) return;
    if (!loadMoreSentinelRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        onLoadMore();
      },
      { root: null, rootMargin: "200px 0px", threshold: 0 }
    );

    observer.observe(loadMoreSentinelRef.current);
    return () => observer.disconnect();
  }, [onLoadMore, hasMore, isFetchingMore]);

  useEffect(() => {
    if (!onLoadMore || !hasMore || isFetchingMore) return;

    const onScroll = () => {
      if (isFetchingMore || !hasMore) return;
      const viewportBottom = window.scrollY + window.innerHeight;
      const pageBottom = document.documentElement.scrollHeight;
      if (pageBottom - viewportBottom <= 260) {
        onLoadMore();
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [onLoadMore, hasMore, isFetchingMore]);

  if (leaderboardError) {
    return (
      <div className="text-center text-red-600">
        <p>Error: {leaderboardError.message}</p>
      </div>
    );
  }

  const leaderboard = leaderboardData?.getLeaderboard || [];
  const notStartedStudents = leaderboard.filter(
    (student) => student.totalScore === 0
  );
  const filteredLeaderboard = leaderboard.filter(
    (student) => student.totalScore > 0
  );

  const top20Students = filteredLeaderboard.slice(0, 20);
  const least20Students =
    filteredLeaderboard.length > 20 ? filteredLeaderboard.slice(-20) : [];

  
  return (
    <div className="p-4 pt-2 bg-white rounded-lg shadow dark:bg-gray-800 sm:p-6 sm:pt-3 xl:p-8 xl:pt-4 font-poppins">
      {leaderboard.length > 0 ? (
        <>
          {[
            { title: "Top 20 Students", students: top20Students, tourId: "top-20-students" },
            {
              title: "Least 20 Students (Need to be monitored)",
              students: least20Students,
              tourId: "least-20-students",
            },
            { title: "Not Yet Started", students: notStartedStudents, tourId: "not-yet-started" },
          ].map(
            ({ title, students, tourId }) =>
              students.length > 0 && (
                <div key={title} className="mb-6">
                  <h4 className="mb-4 text-xl font-bold font-poppins" data-tour={tourId}>{title}</h4>
                  <div className="overflow-x-auto rounded-sm shadow">
                    <Table className="min-w-full text-center font-poppins">
                      <Table.Head style={{backgroundColor: '#5c7a94'}} className="text-white">
                        <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="text-white">S.No</Table.HeadCell>
                        <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="text-white">Student Email</Table.HeadCell>
                        <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="text-white">Last Submission</Table.HeadCell>
                        <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="text-white">Total Score</Table.HeadCell>
                        <Table.HeadCell style={{backgroundColor: '#5c7a94'}} className="text-white">Questions Completed</Table.HeadCell>
                      </Table.Head>
                      <Table.Body>
                        {students.map((student, index) => (
                          <Table.Row key={index} className="hover:bg-gray-50 font-poppins">
                            <Table.Cell>{index + 1}</Table.Cell>
                            <Table.Cell>{student.student.email}</Table.Cell>
                            <Table.Cell>
                              {student.latestSubmissionDate
                                ? formatDate(student.latestSubmissionDate)
                                : "Not yet started"}
                            </Table.Cell>
                            <Table.Cell>{student.totalScore}</Table.Cell>
                            <Table.Cell>
                              {student.student.completedQuestions?.length || 0}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                </div>
              )
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-[500px] text-center">
          <MdOutlinePersonOff className="mb-4 text-gray-400 text-7xl" />
          <h2 className="mb-2 text-2xl font-bold text-gray-600">
            Oops, No students are enrolled in this batch
          </h2>
          <p className="text-gray-500 mb-6 lg:w-[500px]">
            It looks like we couldn't find any matching data. Try refining the
            filter options.
          </p>
        </div>
      )}
      {isFetchingMore && (
        <div className="flex items-center justify-center py-3">
          <Spinner size="sm" />
          <span className="ml-2 text-xs text-gray-500">Loading more students...</span>
        </div>
      )}
      {!hasMore && leaderboard.length > 0 && (
        <p className="py-2 text-center text-xs text-gray-400">No more students to load.</p>
      )}
      <div ref={loadMoreSentinelRef} className="h-2 w-full" aria-hidden="true" />
    </div>
  );
};

export default LeaderboardStaff;
